import { ResourcesConfig } from 'aws-amplify';

import { environment } from '@ninety/mobile/platform/environments';

import { AUTHORIZATION_SCOPES, webRedirectUri } from './oauth/authorization-request';

/**
 * How Amplify reaches the Authorization Server.
 *
 * Exported on its own as well as inside {@link amplifyConfiguration} because `provideAuth()` has
 * to hand this section to `setAuthConfig()`, and on `ResourcesConfig` every section is optional.
 *
 * **Amplify does not run the federated Grant, so this `oauth` section describes it rather than
 * driving it.** The browser leg is `authorizationUrl` and `redeemAuthorizationCode`; the reasons
 * `signInWithRedirect` cannot do it in a Capacitor WebView are recorded on both. What Amplify does
 * read from here is `signOut()`, which needs the section to be present and well-formed before it
 * will tear a session down at all.
 *
 * `responseType` is `code` because the authorization code flow is the only flow the app performs
 * (ADR 0002). The redirects are the web ones on both platforms: `authorizationRedirectUri()` is what
 * the app actually sends, and the only Amplify path that would read these is the hosted-UI sign-out
 * redirect, which `redeemAuthorizationCode` deliberately never enables.
 */
export const cognitoConfiguration: NonNullable<ResourcesConfig['Auth']> = {
  Cognito: {
    userPoolId: environment.userPoolId,
    userPoolClientId: environment.userPoolWebClientId,
    loginWith: {
      oauth: {
        domain: environment.cognitoDomain,
        // One definition, shared with the authorization request — see `AUTHORIZATION_SCOPES`.
        scopes: [...AUTHORIZATION_SCOPES],
        redirectSignIn: [webRedirectUri()],
        redirectSignOut: [webRedirectUri()],
        responseType: 'code',
        providers: ['Google'],
      },
    },
  },
};

/**
 * Lives beside `provideAuth()` because configuring Amplify and pointing it at the Vault are one
 * operation that has to happen in one order — the initializer there explains why splitting them
 * fails silently.
 */
export const amplifyConfiguration: ResourcesConfig = { Auth: cognitoConfiguration };
